// The Briefing page — "What Merlin is watching for you".
//
// Since kanban 001b6e2b (2026-10-21, the ChemMasters admin: "I'd like to
// completely replace my digest with watches. I don't think there's any reason
// to have them be separate things") everything Merlin writes for a reader is a
// WATCH, and this page is one row of them:
//   (a) the tabs: the reader's daily briefing first (the former personal
//       digest — a watch of kind `briefing`, same cadence / pause / remove as
//       any other), then their other watches, then "Watch something new". A
//       reader with no briefing gets a leading "Start my daily briefing" tab;
//   (b) the selected watch's readout and controls (the shared WatchPanel);
//   (c) beside it, "What Merlin remembers" (the reader's own memory bank,
//       which shapes every watch) and ONE chat with Merlin — what they say
//       about a watch ("add a line per CSR", "watch October sales every 48
//       hours", "we're done with this") goes through Merlin's watch tools.
// Admin oversight of everyone's watches is the Watches page (`?scope=all`).

import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BriefingStart } from '../components/merlinWatches/BriefingStart';
import { WatchNewForm } from '../components/merlinWatches/WatchNewForm';
import { WatchPanel } from '../components/merlinWatches/WatchPanel';
import { WatchRail, type RailLeadTab } from '../components/merlinWatches/WatchRail';
import { NEW_WATCH, useWatches } from '../components/merlinWatches/useWatches';
import { DigestChat } from '../components/personDigest/DigestChat';
import { ReaderMemoryPanel } from '../components/personDigest/ReaderMemoryPanel';
import { PortalSignalsTabs } from '../components/signals/PortalSignalsTabs';
import { SignalsTabs } from '../components/signals/SignalsTabs';
import { useAuth } from '../context/AuthContext';
import { findBriefing } from './merlinWatchesUtils';
import { firstName } from './personDigestUtils';
import styles from './BriefingPage.module.css';

/** The leading tab while the reader has no daily briefing. */
const START_BRIEFING = 'start_briefing';

export function BriefingPage({ variant = 'admin' }: { variant?: 'admin' | 'portal' } = {}) {
  const isPortal = variant === 'portal';
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  const [searchParams, setSearchParams] = useSearchParams();
  const [memoryKey, setMemoryKey] = useState(0);

  // `?watch=<id>` (or `?watch=new`) deep-links to one tab; with nothing
  // asked for, the daily briefing is selected.
  const watchParam = searchParams.get('watch');
  const watches = useWatches('mine', {
    autoSelect: 'none',
    initialSelected: watchParam === 'new' ? NEW_WATCH : watchParam,
  });
  const briefing = findBriefing(watches.items);
  const panel = watches.selected ?? START_BRIEFING;

  // A briefing started from the chat (or anywhere) while nothing is selected
  // takes over the leading tab it replaces.
  const briefingId = briefing?.id ?? null;
  const { selected, select } = watches;
  useEffect(() => {
    if (selected === null && briefingId) select(briefingId);
  }, [selected, briefingId, select]);

  useEffect(() => {
    const current = searchParams.get('watch');
    const wanted = watches.selected === NEW_WATCH ? 'new' : watches.selected;
    if ((wanted ?? null) === (current ?? null)) return;
    const next = new URLSearchParams(searchParams);
    if (wanted) next.set('watch', wanted);
    else next.delete('watch');
    setSearchParams(next, { replace: true });
  }, [watches.selected, searchParams, setSearchParams]);

  const selectTab = useCallback(
    (id: string) => watches.select(id === START_BRIEFING ? null : id),
    [watches],
  );

  // A chat turn may have recorded an ask or changed a watch.
  const afterTurn = useCallback(() => {
    setMemoryKey((k) => k + 1);
    void watches.refreshAll();
  }, [watches]);

  const leading: RailLeadTab[] = briefing
    ? []
    : [
        {
          id: START_BRIEFING,
          label: 'Start my daily briefing',
          meta: 'Weekday mornings',
          status: 'Not started',
          action: 'briefing_start_tab',
        },
      ];

  const readerFirst = firstName(user?.name ?? '') || 'there';
  const selectedWatch = watches.detail?.watch ?? null;
  const chatWatch =
    panel !== START_BRIEFING && panel !== NEW_WATCH && selectedWatch && selectedWatch.id === panel
      ? { id: selectedWatch.id, subject: selectedWatch.subject }
      : null;

  return (
    <div className={styles.page}>
      {isPortal ? <PortalSignalsTabs /> : <SignalsTabs />}

      <header className={styles.head}>
        <h1>What Merlin is watching for you</h1>
        <p>
          Your daily briefing first, then each thing you asked Merlin to watch. Tell Merlin in the
          chat what to change or how you like to read; it keeps that for every watch.
        </p>
        {watches.note && <div className={styles.note}>{watches.note}</div>}
      </header>

      {watches.loading && <div className={styles.muted}>Loading…</div>}
      {watches.error && <div className={styles.error}>{watches.error}</div>}

      {!watches.loading && !watches.error && (
        <>
          <WatchRail
            layout="top"
            ariaLabel="Your watches"
            leading={leading}
            items={watches.sorted}
            selected={panel}
            onSelect={selectTab}
          />

          <div className={styles.grid}>
            {panel === START_BRIEFING && (
              <section className={styles.reportCol} aria-label="Daily briefing">
                <BriefingStart
                  busy={watches.busy}
                  onStart={(subject) => watches.createWatch(subject, 'daily', 'briefing')}
                />
              </section>
            )}

            {panel === NEW_WATCH && (
              <section className={styles.reportCol} aria-label="New watch">
                <WatchNewForm busy={watches.busy} onCreate={watches.createWatch} chatHint />
              </section>
            )}

            {panel !== START_BRIEFING && panel !== NEW_WATCH && (
              <section className={styles.reportCol} aria-label="Watch">
                {watches.detail && watches.detail.watch.id === panel ? (
                  <WatchPanel
                    detail={watches.detail}
                    html={watches.html}
                    isOwner={!!user && watches.detail.watch.owner_user_id === user.id}
                    busy={watches.busy}
                    onCadence={(c) => void watches.setCadence(c)}
                    onRunNow={() => void watches.runNow()}
                    onTogglePause={() => void watches.togglePause()}
                    onRemove={watches.remove}
                    siblings={watches.sorted}
                    onSelectWatch={selectTab}
                    refineSlot={
                      <p className={styles.chatHint} data-testid="watch-refine-hint">
                        To change what it shows, or how it reads, tell Merlin in the chat on
                        the right: “bullet points for this section”, “I also want to see…”.
                        Merlin rewrites the readout now and keeps it that way on every later
                        check at your cadence. Charts stay unless you say otherwise.
                      </p>
                    }
                  />
                ) : (
                  <div className={styles.muted}>Loading watch…</div>
                )}
              </section>
            )}

            <aside className={styles.sideCol}>
              <ReaderMemoryPanel
                readerName={readerFirst}
                userId={user?.id ?? null}
                isAdmin={isAdmin}
                refreshKey={memoryKey}
              />
              <div className={styles.chatWrap}>
                <DigestChat
                  readerName={readerFirst}
                  onTurnComplete={afterTurn}
                  mode={chatWatch ? 'watch' : 'general'}
                  watch={chatWatch}
                />
              </div>
            </aside>
          </div>
        </>
      )}
    </div>
  );
}
